import Icon, {
  DashboardOutlined,
  LeftOutlined,
  LogoutOutlined,
  RightOutlined,
  UnorderedListOutlined,
} from "@ant-design/icons";
import { css, cx } from "@emotion/css";
import { Sider, useThemedLayoutContext } from "@refinedev/antd";
import type { ITreeMenu } from "@refinedev/core";
import {
  CanAccess,
  useActiveAuthProvider,
  useIsExistAuthentication,
  useLink,
  useLogout,
  useMenu,
  useRefineContext,
  useRouterContext,
  useRouterType,
  useTranslate,
  useWarnAboutChange,
} from "@refinedev/core";
import { Button, ConfigProvider, Drawer, Grid, Layout, Menu, theme } from "antd";
import type { CSSProperties, FC, ReactNode } from "react";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import CollapseIcon from "./assets/collapse.svg?react";
import ExpendIcon from "./assets/expend.svg?react";
import RefreshIcon from "./assets/refresh.svg?react";

const { SubMenu } = Menu;
const { useToken } = theme;

interface ICustomSider {
  Title?: FC<any>;
  icon: ReactNode;
}
// @ts-ignore
const CustomSider: FC<ICustomSider> = ({ Title }) => (
  <ConfigProvider>
    <Sider
      render={({ items, logout }) => (
        <>
          {items} {logout}
        </>
      )}
      // @ts-ignore
      Title={Title}
    />
  </ConfigProvider>
);

export const _Sider: React.FC<any> = ({
  Title = "Pabbit Admin",
  icon,
  render,
  meta,
  fixed,
  activeItemDisabled = false,
  onRefresh,
  refreshState,
  children,
}) => {
  const isExistAuthentication = useIsExistAuthentication();
  const routerType = useRouterType();
  const NewLink = useLink();
  const { warnWhen, setWarnWhen } = useWarnAboutChange();
  const { Link: LegacyLink } = useRouterContext();
  const Link = routerType === "legacy" ? LegacyLink : NewLink;
  const translate = useTranslate();
  const { menuItems, selectedKey, defaultOpenKeys } = useMenu({ meta });
  const breakpoint = Grid.useBreakpoint();
  const { hasDashboard } = useRefineContext();
  const authProvider = useActiveAuthProvider();
  const { mutate: mutateLogout } = useLogout({
    v3LegacyAuthProviderCompatible: Boolean(authProvider?.isLegacy),
  });
  const { token } = useToken();
  const { siderCollapsed, setSiderCollapsed, mobileSiderOpen, setMobileSiderOpen } = useThemedLayoutContext();
  const navigate = useNavigate();
  const [openKey, setOpenKey] = useState("");
  const path = useLocation().pathname;
  useEffect(() => {
    if (!openKey) {
      setOpenKey(selectedKey);
    }
  }, [selectedKey]);
  // When click back button on 404 page, the dashboard menu can be selected
  useEffect(() => {
    if (path === "/analytics") {
      setOpenKey("/admin_dashboard");
    } else if (path?.includes("/properties")) {
      setOpenKey("/property_list");
    } else if (path?.includes("/devices") && !path?.includes("/devices-config")) {
      setOpenKey("/device_list");
    }
  }, [path]);

  const isMobile = typeof breakpoint.lg === "undefined" ? false : !breakpoint.lg;

  const toggleSider = () => {
    if (isMobile) {
      setMobileSiderOpen(!mobileSiderOpen);
    } else {
      setSiderCollapsed(!siderCollapsed);
    }
  };

  const ThemeTitle = () => (
    <div className="flex max-h-16 w-full items-center" style={{ marginLeft: siderCollapsed ? 16 : 0 }}>
      {!siderCollapsed && (
        <div className="mr-12 hidden w-32 gap-2 text-white lg:flex">
          {icon}
          <div className="whitespace-nowrap text-base font-semibold">{Title}</div>
        </div>
      )}
      <div className="ml-2 flex gap-6 text-2xl">
        <Icon className="cursor-pointer" component={CollapseIcon} onClick={toggleSider} />
        <Icon
          className={cx("cursor-pointer", { "animate-spin": refreshState })}
          component={RefreshIcon}
          onClick={(event) => {
            event.stopPropagation();
            onRefresh?.();
          }}
        />
        {children}
      </div>
    </div>
  );

  const renderTreeView = (tree: ITreeMenu[], selectedKey?: string) => {
    return tree.map((item: ITreeMenu) => {
      const { icon, label, route, key, name, children } = item;

      if (children.length > 0) {
        return (
          <CanAccess
            action="list"
            key={item.key}
            params={{
              resource: item,
            }}
            resource={name.toLowerCase()}
          >
            <SubMenu icon={icon ?? <UnorderedListOutlined />} key={item.key} title={label}>
              {renderTreeView(children, selectedKey)}
            </SubMenu>
          </CanAccess>
        );
      }
      const isSelected = key === selectedKey;

      const linkStyle: CSSProperties = activeItemDisabled && isSelected ? { pointerEvents: "none" } : {};

      return (
        <CanAccess
          action="list"
          key={item.key}
          params={{
            resource: item,
          }}
          resource={name.toLowerCase()}
        >
          <Menu.Item
            className={cx(css`
              .ant-menu-item:not(.ant-menu-item-selected) > svg,
              .ant-menu-submenu:not(.ant-menu-submenu-selected) svg {
                color: #909899 !important;
              }
            `)}
            icon={item.meta?.icon}
            key={item.key}
            onClick={() => {
              navigate(route ?? "");
            }}
            style={linkStyle}
          >
            <div className="overflow-hidden overflow-ellipsis whitespace-nowrap">{label}</div>
            {!siderCollapsed && isSelected && <div className="ant-menu-tree-arrow" />}
          </Menu.Item>
        </CanAccess>
      );
    });
  };

  const handleLogout = () => {
    if (warnWhen) {
      const confirm = window.confirm(
        translate("warnWhenUnsavedChanges", "Are you sure you want to leave? You have unsaved changes.")
      );

      if (confirm) {
        setWarnWhen(false);
        mutateLogout();
      }
    } else {
      mutateLogout();
    }
  };

  const logout = isExistAuthentication && (
    <Menu.Item icon={<LogoutOutlined />} key="logout" onClick={() => handleLogout()}>
      {translate("buttons.logout", "Logout")}
    </Menu.Item>
  );

  const dashboard = hasDashboard ? (
    <Menu.Item icon={<DashboardOutlined />} key="dashboard">
      <Link to="/">{translate("dashboard.title", "Dashboard")}</Link>
      {!siderCollapsed && selectedKey === "/" && <div className="ant-menu-tree-arrow" />}
    </Menu.Item>
  ) : null;

  const items = renderTreeView(menuItems, selectedKey);

  const renderSider = () => {
    if (render) {
      return render({
        dashboard,
        items,
        logout,
        collapsed: siderCollapsed,
      });
    }
    return (
      <>
        {dashboard}
        {items}
      </>
    );
  };

  const renderMenu = () => {
    return (
      <Menu
        defaultOpenKeys={defaultOpenKeys}
        expandIcon={<ExpendIcon />}
        inlineIndent={16}
        mode="inline"
        onClick={() => {
          setMobileSiderOpen(false);
        }}
        onSelect={({ key }) => {
          setOpenKey(key);
        }}
        selectedKeys={[openKey]}
        style={{
          paddingTop: "4px",
          border: "none",
          // overflow: "auto",
          height: "calc(100% - 72px)",
        }}
      >
        {renderSider()}
      </Menu>
    );
  };

  const renderDrawerSider = () => {
    return (
      <>
        <div className="fixed left-0 top-0">
          <div
            style={{
              width: "180px",
              padding: "0 16px",
              display: "flex",
              justifyContent: "flex-start",
              alignItems: "center",
              height: "64px",
              // borderBottom: `1px solid ${token.colorBorder}`, // BUG: remove unnecessary border
            }}
          >
            <ThemeTitle />
          </div>
        </div>
        <Drawer
          closable={false}
          maskClosable={true}
          onClose={() => setMobileSiderOpen(false)}
          open={mobileSiderOpen}
          placement="left"
          styles={{ body: { padding: 0 } }}
          width={180}
        >
          <Layout>
            <Layout.Sider
              style={{
                height: "100vh",
                backgroundColor: token.colorBgContainer,
                borderRight: `1px solid ${token.colorBgElevated}`,
              }}
            >
              {renderMenu()}
            </Layout.Sider>
          </Layout>
        </Drawer>
      </>
    );
  };

  if (isMobile) {
    return renderDrawerSider();
  }

  const siderStyles: React.CSSProperties = {
    backgroundColor: token.colorBgContainer,
    borderRight: `1px solid ${token.colorBgElevated}`,
  };

  if (fixed) {
    siderStyles.position = "fixed";
    siderStyles.top = 0;
    siderStyles.height = "100vh";
    siderStyles.zIndex = 999;
  }

  return (
    <>
      {fixed && (
        <div
          style={{
            width: siderCollapsed ? "80px" : "180px",
            transition: "all 0.2s",
          }}
        />
      )}
      <Layout.Sider
        breakpoint="lg"
        collapsed={siderCollapsed}
        collapsedWidth={0.1}
        collapsible
        onCollapse={(collapsed, type) => {
          if (type === "clickTrigger") {
            setSiderCollapsed(collapsed);
          }
        }}
        style={siderStyles}
        trigger={
          <Button
            className={siderCollapsed ? "invisible" : ""}
            style={{
              borderRadius: 0,
              height: "100%",
              width: "100%",
              backgroundColor: token.colorBgElevated,
            }}
            type="text"
          >
            {siderCollapsed ? (
              <RightOutlined
                style={{
                  color: token.colorPrimary,
                }}
              />
            ) : (
              <LeftOutlined
                style={{
                  color: token.colorPrimary,
                }}
              />
            )}
          </Button>
        }
      >
        <ConfigProvider
          theme={{
            components: { Typography: { colorTextHeading: token.colorWhite } },
          }}
        >
          <div
            style={{
              width: 210,
              padding: siderCollapsed ? "0" : "0 16px",
              display: "flex",
              justifyContent: siderCollapsed ? "center" : "flex-start",
              alignItems: "center",
              height: "64px",
              backgroundColor: token.colorPrimary,
              fontSize: "14px",
              // // borderBottom: `1px solid ${token.colorBorder}`, // BUG: remove unnecessary border
            }}
          >
            <ThemeTitle />
          </div>
        </ConfigProvider>
        {renderMenu()}
      </Layout.Sider>
    </>
  );
};

export default _Sider;
